import { useState, useEffect } from 'react'

export function ConfirmDialog({ title, message, confirmLabel = '実行', confirmDanger = false, onConfirm, onClose }: {
  title: string
  message: string
  confirmLabel?: string
  confirmDanger?: boolean
  onConfirm: () => Promise<void> | void
  onClose: () => void
}) {
  const [running, setRunning] = useState(false)
  const handleConfirm = async () => {
    setRunning(true)
    try { await onConfirm() }
    finally { setRunning(false) }
  }
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={running}>キャンセル</button>
          <button className={`btn ${confirmDanger ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleConfirm} disabled={running}>
            {running ? '処理中...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 500,
      background: ok ? 'var(--toast-ok-bg)' : 'var(--toast-ng-bg)',
      color: ok ? 'var(--toast-ok-text)' : 'var(--toast-ng-text)',
      padding: '12px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: 380,
    }}>
      <span>{ok ? '✓' : '⚠'}</span>
      <span style={{ flex: 1, whiteSpace: 'pre-line' }}>{msg}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'inherit', fontSize: 14, opacity: 0.7, padding: 0,
      }}>✕</button>
    </div>
  )
}

// ================================================================
// Undo つき Toast（保存直後に取り消しボタンを表示）
// countdown: 秒数カウントダウンを表示
// ================================================================
export function UndoToast({ msg, onUndo, onClose, seconds = 8, extraAction }: {
  msg: string
  onUndo: () => Promise<void>
  onClose: () => void
  seconds?: number
  extraAction?: { label: string; onClick: () => void }
}) {
  const [remaining, setRemaining] = useState(seconds)
  const [undoing, setUndoing] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(timer); onClose(); return 0 }
        return r - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const handleUndo = async () => {
    setUndoing(true)
    try { await onUndo(); onClose() }
    catch { setUndoing(false) }
  }

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 500,
      background: 'var(--toast-ok-bg)', color: 'var(--toast-ok-text)',
      padding: '12px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 400,
    }}>
      <span>✓</span>
      <span style={{ flex: 1 }}>{msg}</span>
      {extraAction && (
        <button onClick={() => { extraAction.onClick(); onClose() }} style={{
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
          color: 'var(--toast-ok-text)', borderRadius: 4, padding: '3px 8px',
          cursor: 'pointer', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
        }}>
          {extraAction.label}
        </button>
      )}
      <button onClick={handleUndo} disabled={undoing} style={{
        background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
        color: 'var(--toast-ok-text)', borderRadius: 4, padding: '3px 10px',
        cursor: undoing ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        {undoing ? '取り消し中...' : `↩ 元に戻す (${remaining})`}
      </button>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'inherit', fontSize: 14, opacity: 0.6, padding: 0,
      }}>✕</button>
    </div>
  )
}

export function ClosedBanner({ month, closedAt }: { month: string; closedAt?: string }) {
  return (
    <div style={{
      background: 'var(--blue-light)', border: '1px solid rgba(30,95,168,0.2)',
      borderRadius: 8, padding: '10px 16px', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{ fontSize: 16 }}>🔒</span>
      <div>
        <span style={{ fontWeight: 700, color: 'var(--blue)', fontSize: 13 }}>
          {month} は締め済みです
        </span>
        {closedAt && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 8 }}>
            （{closedAt.slice(0, 10)} 締め処理）
          </span>
        )}
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
          固定費・支出の支払状態は変更できません。月次支払（アシスタント）の支払完了記録は引き続き可能です。
        </div>
      </div>
    </div>
  )
}